import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORIES, catLabel } from "@/lib/constants";
import { Bell, Heart, Users, CreditCard, ShieldOff, HelpCircle, Trash2, AtSign, Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "notifications", label: "Notifications", icon: Bell },
  { k: "preferred",     label: "Preferred Feed", icon: Heart },
  { k: "blocked",       label: "Blocked Users", icon: ShieldOff },
  { k: "payments",      label: "Payment History", icon: CreditCard },
  { k: "username",      label: "Username", icon: AtSign },
  { k: "support",       label: "Support", icon: HelpCircle },
  { k: "delete",        label: "Delete Account", icon: Trash2 },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("notifications");
  if (!user) return null;
  return (
    <div data-testid="settings-page" className="min-h-screen pb-24">
      <TopBar subtitle="Settings"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app/me" label="Back"/>
        <div className="flex flex-wrap gap-2">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.k} data-testid={`settings-tab-${t.k}`} onClick={() => setTab(t.k)} className={`h-9 px-3.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5 transition-colors ${tab === t.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700"}`}>
                <Icon size={13}/> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "notifications" && <NotificationsPanel/>}
        {tab === "preferred"     && <PreferredFeedPanel/>}
        {tab === "blocked"       && <BlockedUsersPanel/>}
        {tab === "payments"      && <PaymentHistoryPanel/>}
        {tab === "username"      && <UsernamePanel me={user}/>}
        {tab === "support"       && <SupportPanel/>}
        {tab === "delete"        && <DeleteAccountPanel me={user}/>}
      </div>
    </div>
  );
}

function NotificationsPanel() {
  const [prefs, setPrefs] = useState(null);
  const load = async () => { const { data } = await api.get("/notifications/settings"); setPrefs(data); };
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    setPrefs(p => ({ ...p, ...patch }));
    try { await api.patch("/notifications/settings", patch); toast.success("Saved"); }
    catch { toast.error("Save failed"); load(); }
  };
  if (!prefs) return <PanelLoader/>;
  const opts = [
    {k: "likes",       label: "Likes on my performance"},
    {k: "comments",    label: "New comments"},
    {k: "follows",     label: "New followers"},
    {k: "uploads",     label: "New uploads from creators I follow"},
    {k: "weekly_summaries",       label: "Weekly opportunity summaries"},
    {k: "creator_pass_reminders", label: "Creator Pass / System updates"},
  ];
  return (
    <Panel testid="notif-tab" title="Notification Preferences" hint={`Plan: ${prefs.plan || "free"} · ${prefs.push_daily_cap} pushes/day · Quiet hours ${prefs.quiet_hours?.start_ist ?? 23}:00 – 0${prefs.quiet_hours?.end_ist ?? 8}:00 IST`}>
      {opts.map(o => (
        <label key={o.k} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 mb-2">
          <span className="text-sm text-white">{o.label}</span>
          <input data-testid={`pref-${o.k}`} type="checkbox" checked={prefs[o.k] !== false} onChange={(e) => save({ [o.k]: e.target.checked })} className="accent-[#DC2626] w-4 h-4"/>
        </label>
      ))}
    </Panel>
  );
}

function PreferredFeedPanel() {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/users/me/preferences"); setSelected(data.preferred_categories || []); }
    finally { setLoading(false); }
  })(); }, []);
  const toggle = async (k) => {
    const next = selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k];
    setSelected(next);
    try { await api.patch("/users/me/preferences", { preferred_categories: next }); toast.success("Preferences saved"); }
    catch { toast.error("Save failed"); }
  };
  if (loading) return <PanelLoader/>;
  return (
    <Panel testid="preferred-tab" title="Preferred Feed" hint="Choose the categories you enjoy watching. Your home feed will show more of these — even if you don't perform in them.">
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(c => (
          <button key={c.key} data-testid={`pref-cat-${c.key}`} onClick={() => toggle(c.key)} className={`h-9 px-3.5 rounded-full text-xs font-medium border transition-colors ${selected.includes(c.key) ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700"}`}>
            {c.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">{selected.length} categor{selected.length === 1 ? "y" : "ies"} selected</p>
    </Panel>
  );
}

function BlockedUsersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users/me/blocked"); setRows(data.users || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const unblock = async (uname) => {
    try { await api.delete(`/users/${uname}/block`); toast.success("Unblocked"); load(); }
    catch { toast.error("Failed"); }
  };
  if (loading) return <PanelLoader/>;
  return (
    <Panel testid="blocked-tab" title="Blocked Users" hint="Blocked users can't follow you, message you, or see your content.">
      {rows.length === 0 ? <p className="text-sm text-zinc-500 text-center py-6">You haven't blocked anyone yet.</p> : (
        <div className="space-y-2">
          {rows.map(u => (
            <div key={u.user_id} data-testid={`blocked-${u.username}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="w-10 h-10 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/30 flex items-center justify-center text-[#DC2626] font-semibold overflow-hidden">
                {u.profile?.avatar || u.picture ? <img src={u.profile?.avatar || u.picture} alt="" className="w-full h-full object-cover"/> : (u.name || "?").slice(0,1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                <p className="text-[11px] text-zinc-500 truncate">@{u.username} · {catLabel(u.category)}</p>
              </div>
              <Button data-testid={`unblock-${u.username}`} onClick={() => unblock(u.username)} className="h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs">Unblock</Button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function PaymentHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/users/me/payments"); setRows(data.payments || []); }
    finally { setLoading(false); }
  })(); }, []);
  if (loading) return <PanelLoader/>;
  return (
    <Panel testid="payments-tab" title="Payment History" hint="Every Creator Pass, verification & spotlight payment on your account.">
      {rows.length === 0 ? <p className="text-sm text-zinc-500 text-center py-6">No payments yet.</p> : (
        <div className="space-y-2">
          {rows.map((p, i) => (
            <div key={p.order_id || i} data-testid={`payment-${i}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">{(p.kind || "plan").toUpperCase()} {p.plan ? `· ${p.plan}` : ""}</p>
                <span className="text-sm font-bold text-[#F5C462]">₹{p.amount || 0}</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">{new Date(p.created_at).toLocaleString()} · {p.status}{p.mock ? " · demo" : ""}</p>
              {p.order_id && <p className="text-[10px] text-zinc-600 font-mono truncate">{p.order_id}</p>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function UsernamePanel({ me }) {
  const [newU, setNewU] = useState(me.username || "");
  const [busy, setBusy] = useState(false);
  const [lastChanged, setLastChanged] = useState(me.username_changed_at || null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/username", { new_username: newU.trim() });
      toast.success(`Username changed to @${data.username}`);
      setLastChanged(new Date().toISOString());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const daysLeft = lastChanged ? Math.max(0, 30 - Math.floor((Date.now() - new Date(lastChanged).getTime()) / 86400000)) : 0;
  return (
    <Panel testid="username-tab" title="Change Username" hint="Username can be changed once every 30 days to prevent spam & impersonation.">
      <Input data-testid="new-username" value={newU} onChange={e => setNewU(e.target.value)} placeholder="new_username" className="bg-zinc-900 border-zinc-800"/>
      {daysLeft > 0 && <p className="text-[11px] text-amber-400 mt-2">Next change available in {daysLeft} day{daysLeft === 1 ? "" : "s"}.</p>}
      <Button data-testid="save-username" onClick={submit} disabled={busy || daysLeft > 0 || !newU.trim()} className="mt-3 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm h-9">
        {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : null} Save username
      </Button>
    </Panel>
  );
}

function SupportPanel() {
  const [kind, setKind] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!subject.trim() || !message.trim()) return toast.error("Subject and message required");
    setBusy(true);
    try {
      await api.post("/support/ticket", { kind, subject: subject.trim(), message: message.trim() });
      toast.success("Ticket submitted. Our team will reach out.");
      setSubject(""); setMessage("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Panel testid="support-tab" title="Report a Bug / Get Help" hint="Our team will reach out via email or WhatsApp typically within 24h.">
      <div className="flex gap-2 mb-3">
        {[{k: "bug", l: "🐞 Bug"}, {k: "help", l: "💬 Help"}, {k: "feedback", l: "✨ Feedback"}].map(o => (
          <button key={o.k} data-testid={`support-kind-${o.k}`} onClick={() => setKind(o.k)} className={`h-9 px-3.5 rounded-full text-xs font-medium border ${kind === o.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{o.l}</button>
        ))}
      </div>
      <Input data-testid="support-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" className="bg-zinc-900 border-zinc-800 mb-2"/>
      <Textarea data-testid="support-message" value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe the issue or feedback..." rows={5} className="bg-zinc-900 border-zinc-800"/>
      <Button data-testid="support-submit" onClick={submit} disabled={busy || !subject.trim() || !message.trim()} className="mt-3 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm h-9">
        {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : null} Submit ticket
      </Button>
    </Panel>
  );
}

function DeleteAccountPanel({ me }) {
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/delete-account", { confirm_username: confirm.trim() });
      toast.success("Account deleted. You will be logged out.");
      setTimeout(() => { localStorage.clear(); window.location.href = "/login"; }, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Panel testid="delete-tab" title="Delete Account" hint="This action hides your profile + all uploads. Recovery is manual — contact support within 30 days.">
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 mb-3">
        <p className="text-xs text-red-300 leading-relaxed">
          <strong className="text-red-400">Warning:</strong> Your profile, videos and follower connections will be hidden immediately. Casting inquiries you've sent stay archived for compliance.
        </p>
      </div>
      <p className="text-[11px] text-zinc-500 mb-2">Type your username <span className="text-white font-semibold">@{me.username}</span> to confirm:</p>
      <Input data-testid="delete-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={me.username} className="bg-zinc-900 border-zinc-800"/>
      <Button data-testid="delete-submit" onClick={submit} disabled={busy || confirm.trim() !== me.username} className="mt-3 rounded-full bg-zinc-700 hover:bg-zinc-600 text-white text-sm h-9">
        {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : null} Delete my account
      </Button>
    </Panel>
  );
}

function Panel({ testid, title, hint, children }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
      <h2 className="font-display text-base font-semibold text-white">{title}</h2>
      {hint && <p className="text-[11px] text-zinc-500 mt-1 mb-4 leading-relaxed">{hint}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function PanelLoader() { return <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-6 flex justify-center text-zinc-500"><Loader2 className="animate-spin"/></div>; }
